import React from 'react';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import { Message, ConversationItem } from './type'
import './ChatSession.scss';

// interface Message {
//   content: string;
//   role: 'user' | 'bot';
//   timestamp: string;
// }

// interface ConversationItem {
//   message: Message;
//   _id: string;
// }

interface Chat {
  _id: string;
  botid: string;
  chatid: string;
  bot_created_date: string;
  conversation: ConversationItem[];
}

interface ChatListProps {
  onChatSelect: (chatId: string, date: string, messages: ConversationItem[]) => void;
  chats: Chat[];
}

interface GroupedChats {
  [date: string]: {
    chatId: string;
    messages: ConversationItem[];
  }[];
}

// Helper: Parse timestamp safely
const parseTimestamp = (timestamp: string): Date | null => {
  try {
    const iso = timestamp.replace(' ', 'T');
    const date = new Date(iso);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
};

// Helper: Format date string like 'Monday, 3 June 2025'
const formatDate = (dateString: string): string => {
  const [day, month, year] = dateString.split('/');
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const ChatList: React.FC<ChatListProps> = ({ onChatSelect, chats }) => {
  const groupChatsByDate = (chats: Chat[]): GroupedChats => {
    const grouped: GroupedChats = {};

    chats.forEach(chat => {
      chat.conversation?.forEach(conversationItem => {
        const parsedDate = parseTimestamp(conversationItem.message?.timestamp);
        if (!parsedDate) return;

        const dateKey = parsedDate.toLocaleDateString('en-GB');

        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }

        if (!grouped[dateKey].some(item => item.chatId === chat._id)) {
          const messagesForDate = chat.conversation.filter(item => {
            const itemDate = parseTimestamp(item.message?.timestamp);
            return itemDate?.toLocaleDateString('en-GB') === dateKey;
          });

          grouped[dateKey].push({ chatId: chat._id, messages: messagesForDate });
        }
      });
    });

    return grouped;
  };

  const groupedChats = groupChatsByDate(chats);

  const sortedDates = Object.keys(groupedChats).sort((a, b) => {
    const [dayA, monthA, yearA] = a.split('/');
    const [dayB, monthB, yearB] = b.split('/');
    const dateA = new Date(+yearA, +monthA - 1, +dayA);
    const dateB = new Date(+yearB, +monthB - 1, +dayB);
    return dateB.getTime() - dateA.getTime();
  });

  const getMessagePreview = (messages: ConversationItem[]): string => {
    const lastMsg = messages.at(-1)?.message?.content ?? '';
    return lastMsg.length > 50 ? `${lastMsg.substring(0, 50)}...` : lastMsg || 'No messages';
  };

  const getTimeRange = (messages: ConversationItem[]): string => {
    if (!messages.length) return '';

    const firstTime = parseTimestamp(messages[0].message.timestamp);
    const lastTime = parseTimestamp(messages.at(-1)?.message?.timestamp || '');

    const format = (d: Date | null) =>
      d ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : 'Invalid';

    return firstTime && lastTime
      ? `${format(firstTime)} - ${format(lastTime)}`
      : format(firstTime);
  };

  return (
    <div className='vstack gap-3'>
      <div className='border-b border-gray-200 py-4 px-5 session-header'>
        <h3 className='text-xl font-bold leading-6 text-gray-900'>Chat History</h3>
        <p>Chat sessions organized by date</p>
      </div>

      <div className='d-flex flex-column gap-3 px-3'>
        {sortedDates.length ? (
          sortedDates.map(date => (
            <div key={date} className='date-group mb-4'>
              <h5 className='date-header text-primary fw-bold mb-3 border-bottom pb-2'>
                <i className='bi bi-calendar3 me-2'></i>
                Chat Date - {date}
              </h5>

              <div className='chat-sessions'>
                {groupedChats[date].map(({ chatId, messages }, index) => (
                  <div
                    key={`${chatId}-${date}-${index}`}
                    className='chat-session-card mb-3 p-3'
                    style={{
                      border: '2px solid #E2E8F0',
                      borderRadius: '0.75rem',
                      backgroundColor: '#F8F9FA',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.borderColor = '#007bff';
                      e.currentTarget.style.backgroundColor = '#ffffff';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.borderColor = '#E2E8F0';
                      e.currentTarget.style.backgroundColor = '#F8F9FA';
                    }}
                  >
                    <div className='d-flex justify-content-between align-items-start'>
                      <div className='chat-info flex-grow-1'>
                        <div className='d-flex align-items-center mb-2'>
                          <i className='bi bi-chat-dots text-primary me-2'></i>
                          <span className='fw-semibold text-dark'>{formatDate(date)}</span>
                          <span className='badge bg-secondary ms-2'>
                            {messages.length} messages
                          </span>
                        </div>

                        <div className='message-preview text-muted small'>
                          <strong>Last message:</strong> {getMessagePreview(messages)}
                        </div>

                        <div className='time-range text-muted small mt-1'>
                          <i className='bi bi-clock me-1'></i>
                          {getTimeRange(messages)}
                        </div>
                      </div>

                      <div className='action-buttons'>
                        <Button
                          variant='outline-primary'
                          size='sm'
                          onClick={() => onChatSelect(chatId, date, messages)}
                        >
                          <i className='bi bi-eye me-1'></i>
                          View Details
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          <Alert variant='warning' className='mx-3'>
            <i className='bi bi-exclamation-triangle me-2'></i>
            No chat history available. Start a conversation to see chat logs here.
          </Alert>
        )}
      </div>
    </div>
  );
};

export default ChatList;
